import {
  infiniteQueryOptions,
  mutationOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  queryOptions,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type {
  DatabaseRowHistory,
  DatabaseRowHistoryGroup,
  RevertResult,
} from '@/api/types'
import { activityQueryParams, type ActivityFilters } from './activity'

/**
 * How many operations a page holds.
 *
 * Operations rather than rows, which is what makes a modest number enough: one
 * agent session that wrote two thousand rows is one line here.
 */
export const ACTIVITY_PAGE_SIZE = 25

interface Page<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

/**
 * A page of operations.
 *
 * No total: counting the operations in a database means grouping every
 * row-history entry it holds, which costs far more than the page itself and was
 * never shown. Whether there is more to read is what `next` says.
 */
interface OperationPage<T> {
  next: string | null
  previous: string | null
  results: T[]
}

/** A stable key for one narrowing of the list. */
function filterKey(filters: ActivityFilters): string {
  const params = activityQueryParams(filters)
  return JSON.stringify(
    Object.keys(params)
      .sort()
      .map((name) => [name, params[name]]),
  )
}

/**
 * What has happened to a database, as operations, newest first.
 *
 * Nothing here is patched by the realtime layer, and that is a decision rather
 * than an omission. This is a record of what happened, read deliberately and
 * usually while something is being worked out; rows arriving into it from other
 * people's tabs would move what somebody is reading, and marking it stale on
 * every row event would refetch a busy database continuously. It is fresh when
 * it is opened, and again after a revert -- which is the only change made from
 * here.
 */
export function databaseActivityQuery(databaseId: number, filters: ActivityFilters) {
  return infiniteQueryOptions({
    queryKey: qk.activity(databaseId, filterKey(filters)),
    queryFn: ({ pageParam }): Promise<OperationPage<DatabaseRowHistoryGroup>> =>
      unwrap(
        getApi().GET('/api/database/rows/database/{database_id}/history/grouped/', {
          params: {
            path: { database_id: databaseId },
            query: {
              limit: ACTIVITY_PAGE_SIZE,
              offset: pageParam,
              ...activityQueryParams(filters),
            },
          },
        }),
      ) as Promise<OperationPage<DatabaseRowHistoryGroup>>,
    initialPageParam: 0,
    getNextPageParam: (last, _pages, lastOffset) =>
      last.next === null ? undefined : lastOffset + ACTIVITY_PAGE_SIZE,
  })
}

export function useDatabaseActivity(databaseId: number, filters: ActivityFilters) {
  return useInfiniteQuery(databaseActivityQuery(databaseId, filters))
}

/**
 * The rows one operation changed.
 *
 * Not paged, and limited: an operation that touched a thousand rows is not a
 * thousand things to read, and the line above it already says how many there
 * were. What this answers is *what kind of change it was* -- which columns, from
 * what to what -- and a sample settles that as well as the whole set would.
 */
export const OPERATION_ROW_LIMIT = 50

export function operationRowsQuery(databaseId: number, actionUuid: string) {
  return queryOptions({
    queryKey: qk.operationRows(databaseId, actionUuid),
    queryFn: (): Promise<Page<DatabaseRowHistory>> =>
      unwrap(
        getApi().GET('/api/database/rows/database/{database_id}/history/', {
          params: {
            path: { database_id: databaseId },
            query: { action_uuid: actionUuid, limit: OPERATION_ROW_LIMIT, offset: 0 },
          },
        }),
      ) as Promise<Page<DatabaseRowHistory>>,
  })
}

export function useOperationRows(
  databaseId: number,
  actionUuid: string,
  enabled: boolean,
) {
  return useQuery({ ...operationRowsQuery(databaseId, actionUuid), enabled })
}

/** What a revert is addressed to: one operation, or the batch it was part of. */
export type RevertTarget =
  { kind: 'operation'; actionUuid: string } | { kind: 'batch'; actionGroup: string }

/**
 * Puts back what a recorded operation did.
 *
 * This is not undo. Undo is short-lived, is scoped to one session and walks a
 * stack; this works from the record, months later, and is the way to reverse
 * what an agent wrote -- which is the reason the endpoint exists in a fork whose
 * main writer is not a person.
 *
 * `force` is the caller's, not a default: a row that has changed since is
 * reported as a conflict and skipped, and overwriting somebody else's later
 * edit is a second decision rather than part of the first.
 *
 * Everything is invalidated on success, for the same reason undo does it: the
 * response says which rows were touched but not what any cached view of them
 * should now hold, and the tab that asked is excluded from its own broadcasts,
 * so nothing is coming to correct it.
 */
export function revertHistoryMutation(queryClient: QueryClient, databaseId: number) {
  return mutationOptions({
    mutationFn: ({
      target,
      force,
    }: {
      target: RevertTarget
      force: boolean
    }): Promise<RevertResult> =>
      unwrap(
        getApi().POST('/api/database/rows/database/{database_id}/history/revert/', {
          params: { path: { database_id: databaseId } },
          body: {
            ...(target.kind === 'operation'
              ? { action_uuid: target.actionUuid }
              : { action_group: target.actionGroup }),
            force,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: qk.all })
    },
  })
}

export function useRevertHistory(databaseId: number) {
  return useMutation(revertHistoryMutation(useQueryClient(), databaseId))
}
